import React, { useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { ArrowRight } from 'lucide-react';
import { track } from '../analytics';
import { requestTurnstileToken } from '../utils/turnstile';
import { useTranslation } from 'react-i18next';

const SignupModal = ({ open, onClose, quoteData }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Obtain human verification token (Cloudflare Turnstile)
      const turnstileToken = await requestTurnstileToken('signup_submit');
      const browserLanguage = navigator.language.split('-')[0];
      // If calculator context provided, send the quote first so redirects won't block it
      try {
        if (quoteData) {
          const payload = {
            intent: 'quote',
            email: values.email,
            organizationName: values.organizationName,
            language: browserLanguage,
            area: quoteData?.area ?? 0,
            selectedAnalytics: quoteData?.selectedAnalytics ?? [],
            analyticsYears: quoteData?.analyticsYears ?? 1,
            selectedCrops: quoteData?.selectedCrops ?? [],
            years: quoteData?.years ?? 1,
            selectedRegion: quoteData?.selectedRegion ?? null,
            crops: quoteData?.cropsText || '',
            calculator: quoteData,
            turnstileToken,
          };
          fetch(`${process.env.REACT_APP_API_URL}/request-quote`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).catch(() => {});
          try { track('trial_quote_context_sent'); } catch {}
        }
      } catch {}
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          organizationName: values.organizationName,
          language: browserLanguage,
          turnstileToken,
        }),
      });
      
      const data = await response.json();

      // If user already exists, redirect to app login
      if (data && data.message === 'User exists') {
        message.info('Account exists — redirecting to login');
        window.location.href = `${process.env.REACT_APP_FIELDOPS_APP_URL}/app/`;
        return;
      }

      // Show success message
      message.success(t('signup.success'));
      setLoading(false);
      onClose();
    } catch (error) {
      message.error(t('signup.error'));
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('signup.title')}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
    >
      <div className="py-4">
        <p className="text-gray-600 mb-6">{t('signup.desc')}</p>
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label={t('signup.email')}
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input size="large" placeholder={t('signup.emailPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="organizationName"
            label={t('signup.org')}
            rules={[{ required: true, message: 'Please enter your organization name' }]}
          >
            <Input size="large" placeholder={t('signup.orgPlaceholder')} />
          </Form.Item>

          <Form.Item className="mb-0 mt-6">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="w-full bg-primary border-primary h-12 text-lg font-semibold"
              icon={<ArrowRight size={20} />}
              iconPosition="end"
            >
              {t('signup.submit')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default SignupModal;
